<template>
  <div class="card px-4 py-3 mean-explanation">
    <b-collapse :open="true" animation="slide">
      <template #trigger="props">
        <div class="mean-explanation-title" role="button">
          <b-icon icon="circle-info" size="is-small" />
          <span>How the washing mean was calculated</span>
          <b-icon :icon="props.open ? 'chevron-up' : 'chevron-down'" size="is-small" />
        </div>
      </template>
      <p v-if="explanations.length === 0" class="has-text-grey mt-2">
        No washing mean was calculated: the Washing mean is unticked, or the site only has client-specific types, which never get a mean.
      </p>
      <template v-else>
        <ol class="mean-explanation-rules mt-2">
          <li>
            For each crate type, the system finds the <strong>latest washing or machine wash declaration</strong> of that type,
            whatever its date, even outside the loaded dates. Washing and machine wash are added together.
          </li>
          <li v-for="pool in pools" :key="'pool-' + pool.type">
            {{ pool.members.join(' and ') }} are one stock here ({{ pool.type }}), so they are walked <strong>together</strong>:
            from the latest declaration of either, and a day counts when either was washed.
            Each size's mean is its own crates ÷ those shared days, so they add up to the {{ pool.type }} figure.
          </li>
          <li>From that day it walks back one day at a time, for at most {{ rules.windowDays }} days.</li>
          <li>
            Only the site's washing days are looked at: <strong>{{ activeDayNames }}</strong>.
            A declaration made on any other day is ignored.
          </li>
          <li>
            A washing day with at least one declaration adds its crates and counts as one day.
            A washing day with no declaration is skipped: it does <strong>not</strong> count as a zero. Holidays are not skipped.
          </li>
          <li>It stops as soon as {{ rules.maxDays }} days have been counted, or after {{ rules.windowDays }} days.</li>
          <li>Mean = crates found ÷ days counted.</li>
        </ol>
        <p class="mt-2">
          The mean then fills the Washed row of each day <strong>after the last production{{ lastProd ? ' (' + lastProd + ')' : '' }}</strong>
          that is a washing day and not a holiday, unless that day has a washing simulation. A declared day always shows its declaration.
        </p>
        <b-collapse
          v-for="explanation in explanations"
          :key="explanation.type"
          :open="false"
          animation="slide"
          class="mean-explanation-type mt-3"
        >
          <template #trigger="props">
            <div class="mean-explanation-type-head" role="button">
              <b-icon :icon="props.open ? 'chevron-down' : 'chevron-right'" size="is-small" />
              <strong>{{ explanation.type }}</strong>
              <span>: {{ explanation.mean.toFixed(1) }} crates / day</span>
              <span class="has-text-grey ml-2">{{ summary(explanation) }}</span>
            </div>
          </template>
          <p v-if="!explanation.anchor" class="has-text-grey mt-2">
            No washing declaration was ever found for this type, so its mean is 0.
          </p>
          <template v-else>
            <p class="mt-2">
              Latest declaration: <strong>{{ dayName(explanation.anchor.weekday) }} {{ explanation.anchor.date }}</strong>
              (#{{ explanation.anchor.id }}, {{ isPool(explanation) ? explanation.anchor.type + ', ' : '' }}{{ explanation.anchor.operation }}, {{ explanation.anchor.num_crates }} crates).
              {{ stopReason(explanation) }}
            </p>
            <p v-if="isPool(explanation)" class="mt-1">
              Split over the same {{ explanation.countedDays }} days:
              <span v-for="(member, index) in explanation.members" :key="member">
                {{ index > 0 ? ' · ' : '' }}<strong>{{ member }}</strong> {{ explanation.totals[member] }} ÷ {{ explanation.countedDays }} = {{ explanation.means[member].toFixed(1) }}
              </span>
            </p>
            <table class="table is-narrow is-fullwidth mt-2">
              <thead>
                <tr>
                  <th>Day</th>
                  <th>Declarations</th>
                  <th class="has-text-right">
                    Crates
                  </th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="day in explanation.days" :key="day.date" :class="{ 'has-text-grey-light': !day.counted && day.movements.length === 0 }">
                  <td class="mean-explanation-day">
                    {{ dayName(day.weekday) }} {{ day.date }}
                  </td>
                  <td>
                    <div v-for="movement in day.movements" :key="movement.id" :title="movement.comment || ''">
                      {{ movement.num_crates }}{{ isPool(explanation) ? ' ' + movement.type : '' }} · {{ movement.operation }}{{ movement.shift ? ' · shift ' + movement.shift : '' }}
                      <span class="has-text-grey">#{{ movement.id }}</span>
                    </div>
                  </td>
                  <td class="has-text-right">
                    {{ day.movements.length > 0 ? day.total : '' }}
                  </td>
                  <td>
                    <b-tag v-if="day.counted" type="is-success" size="is-small">
                      Counted ({{ day.countedSoFar }} / {{ rules.maxDays }})
                    </b-tag>
                    <b-tag v-else-if="!day.active && day.movements.length > 0" type="is-warning" size="is-small">
                      Not a washing day: ignored
                    </b-tag>
                    <span v-else-if="!day.active">Not a washing day</span>
                    <span v-else>No declaration: skipped</span>
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <th colspan="2">
                    {{ explanation.countedDays }} {{ explanation.countedDays === 1 ? 'day' : 'days' }} counted
                  </th>
                  <th class="has-text-right">
                    {{ explanation.total }}
                  </th>
                  <th>{{ explanation.total }} ÷ {{ explanation.countedDays }} = {{ explanation.mean.toFixed(1) }}</th>
                </tr>
              </tfoot>
            </table>
          </template>
        </b-collapse>
      </template>
    </b-collapse>
  </div>
</template>

<script>
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

// The readout behind the Means dropdown's "Explain washing mean" switch: every day the
// API's washing-mean walk looked at, what it found there and whether the day counted.
// It only lays out what the API traced -- nothing is recomputed here, so it cannot
// drift from the number the forecast used.
export default {
  name: 'WashingMeanExplanation',
  props: {
    // `meanExplanations` from GET /forecast, as sent.
    explanations: {
      type: Array,
      required: true
    },
    // The means cutoff, 'YYYY-MM-DD'.
    lastProd: {
      type: String,
      default: ''
    }
  },
  computed: {
    // The walk's limits and washing days are the same for every type of a site.
    rules () {
      return this.explanations[0]
    },
    activeDayNames () {
      // Monday first, the way the sites read a week.
      return [1, 2, 3, 4, 5, 6, 0].filter(day => this.rules.activeDays.includes(day)).map(day => DAY_NAMES[day]).join(', ')
    },
    // Stocks the API walked as one (5.X), so the rules can say why.
    pools () {
      return this.explanations.filter(this.isPool)
    }
  },
  methods: {
    isPool (explanation) {
      return (explanation.members || []).length > 1
    },
    dayName (weekday) {
      return DAY_NAMES[weekday]
    },
    summary (explanation) {
      if (!explanation.anchor) return 'no declaration found'
      return explanation.total + ' crates over ' + explanation.countedDays + ' ' + (explanation.countedDays === 1 ? 'day' : 'days')
    },
    stopReason (explanation) {
      if (explanation.stop === 'maxDays') {
        return 'It stopped after counting ' + this.rules.maxDays + ' days.'
      }
      return 'It walked back the full ' + this.rules.windowDays + ' days and only found ' + explanation.countedDays +
        ' with a declaration, so it averaged over those.'
    }
  }
}
</script>

<style scoped>
.mean-explanation-title,
.mean-explanation-type-head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}
.mean-explanation-title {
  font-weight: bold;
}
.mean-explanation-rules {
  margin-left: 1.5rem;
}
.mean-explanation-type {
  border-top: 1px solid #dbdbdb;
  padding-top: 0.5rem;
}
.mean-explanation-day {
  white-space: nowrap;
}
</style>
